import React, { useEffect, useState } from 'react';
import { BugIcon, XIcon } from '@phosphor-icons/react';
import { useLocation } from '@reach/router';

import { useActiveFeatureFlags } from '#hooks/useActiveFeatureFlags';
import useFeatureFlagDetails from '#hooks/useFeatureFlagDetails';
import useDataLayerEvents from '#hooks/useDataLayerEvents';
import * as styles from '#sass/diagnostics-panel.module.scss';

const formatTime = (timestamp) =>
   new Date(timestamp).toLocaleTimeString(undefined, {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
   });

const FlagsSection = () => {
   const activeFlags = useActiveFeatureFlags();
   const flagDetails = useFeatureFlagDetails();

   if (!activeFlags.length) {
      return <p className={styles.empty}>No flags read on this page yet.</p>;
   }

   return (
      <ul className={styles.list}>
         {activeFlags.map(({ key, value }) => {
            const variationIndex = flagDetails[key]?.variationIndex;
            return (
               <li key={key} className={styles.listItem}>
                  <code className={styles.flagKey}>{key}</code>
                  <span className={styles.flagValue}>{JSON.stringify(value)}</span>
                  <span className={styles.flagVariation}>
                     variation: {typeof variationIndex === 'number' ? variationIndex : 'unavailable'}
                  </span>
               </li>
            );
         })}
      </ul>
   );
};

const EventsSection = () => {
   const events = useDataLayerEvents();

   if (!events.length) {
      return <p className={styles.empty}>No dataLayer events yet.</p>;
   }

   return (
      <ul className={styles.list}>
         {events.map((entry, index) => (
            <li key={`${entry.timestamp}-${index}`} className={styles.listItem}>
               <details>
                  <summary className={styles.eventSummary}>
                     <span className={styles.eventName}>{entry.event}</span>
                     <span className={styles.eventTime}>{formatTime(entry.timestamp)}</span>
                  </summary>
                  <pre className={styles.eventPayload}>
                     {JSON.stringify(entry.payload, null, 2)}
                  </pre>
               </details>
            </li>
         ))}
      </ul>
   );
};

const DiagnosticsPanel = () => {
   const location = useLocation();
   const [mounted, setMounted] = useState(false);
   const [isOpen, setIsOpen] = useState(false);
   const isShowcase = location.pathname.startsWith('/showcase');
   const themeClass = isShowcase ? styles.showcaseTheme : styles.homeTheme;
   const panelTitle = isShowcase ? '⌁ ORBITAL.DIAG // NODE-7' : 'THE GATSBY CONSOLE';
   const statusLabel = isShowcase ? '● SIGNAL LOCKED' : '◆ SOIRÉE ONLINE';

   useEffect(() => {
      setMounted(true);
   }, []);

   useEffect(() => {
      if (!isOpen) return undefined;

      const onKey = (e) => {
         if (e.key === 'Escape') setIsOpen(false);
      };

      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
   }, [isOpen]);

   if (!mounted) return null;

   return (
      <div className={styles.root}>
         {isOpen && (
            <section
               id="diagnostics-panel"
               aria-label="Site diagnostics"
               className={`${styles.panel} ${themeClass}`}
            >
               <div className={styles.panelHeader}>
                  <div>
                     <h2 className={styles.panelTitle}>{panelTitle}</h2>
                     <span className={styles.status}>{statusLabel}</span>
                  </div>
                  <button
                     type="button"
                     className={styles.closeBtn}
                     onClick={() => setIsOpen(false)}
                     aria-label="Close diagnostics"
                  >
                     <XIcon size={16} weight="bold" />
                  </button>
               </div>
               <div className={styles.panelBody}>
                  <h3 className={styles.sectionTitle}>Feature flags</h3>
                  <FlagsSection />
                  <h3 className={styles.sectionTitle}>DataLayer events</h3>
                  <EventsSection />
               </div>
            </section>
         )}
         <button
            type="button"
            className={`${styles.toggle} ${themeClass}`}
            aria-expanded={isOpen}
            aria-controls="diagnostics-panel"
            aria-label={isOpen ? 'Close diagnostics' : 'Open diagnostics'}
            onClick={() => setIsOpen((open) => !open)}
         >
            <BugIcon size={20} weight="bold" />
         </button>
      </div>
   );
};

export default DiagnosticsPanel;
